/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from  antd/lib/input/textArea
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import RcTextArea from 'rc-textarea';
import omit from 'rc-util/lib/omit';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import ClearableLabeledInput from './ClearableLabeledInput';
import { ConfigContext } from '../config-provider';
import AnimationContext from '../config-provider/AnimationContext';
import { fixControlledValue, resolveOnChange, triggerFocus, getInputClassNames } from './Input';
import SizeContext from '../config-provider/SizeContext';
import warning from 'antd/es/_util/warning';
function fixEmojiLength(value, maxLength) {
    return [...(value || '')].slice(0, maxLength).join('');
}
const TextArea = React.forwardRef(({ prefixCls: customizePrefixCls, bordered = true, showCount = false, maxLength, className, style, size: customizeSize, onCompositionStart, onCompositionEnd, onChange, ...props }, ref) => {
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const animation = React.useContext(AnimationContext);
    const size = React.useContext(SizeContext);
    const innerRef = React.useRef(null);
    const clearableInputRef = React.useRef(null);
    const [compositing, setCompositing] = React.useState(false);
    const [value, setValue] = useMergedState(props.defaultValue, {
        value: props.value
    });
    const handleSetValue = (val, callback) => {
        if (props.value === undefined) {
            setValue(val);
            callback?.();
        }
    };
    if (process.env.NODE_ENV !== 'production')
        warning(props.theme !== 'gray', 'input.textarea', `'themes = gray' is deprecated now`);
    warning(bordered !== false, 'input.group', `'bordered = false' is deprecated now`);
    // =========================== Value Update ===========================
    // Max length value
    const hasMaxLength = Number(maxLength) > 0;
    const onInternalCompositionStart = (e) => {
        setCompositing(true);
        onCompositionStart?.(e);
    };
    const onInternalCompositionEnd = (e) => {
        setCompositing(false);
        let triggerValue = e.currentTarget.value;
        if (hasMaxLength) {
            triggerValue = fixEmojiLength(triggerValue, maxLength);
        }
        // Patch composition onChange when value changed
        if (triggerValue !== value) {
            handleSetValue(triggerValue);
            resolveOnChange(e.currentTarget, e, onChange, triggerValue);
        }
        onCompositionEnd?.(e);
    };
    const handleChange = (e) => {
        let triggerValue = e.target.value;
        if (!compositing && hasMaxLength) {
            triggerValue = fixEmojiLength(triggerValue, maxLength);
        }
        handleSetValue(triggerValue);
        resolveOnChange(e.currentTarget, e, onChange, triggerValue);
    };
    // ============================== Reset ===============================
    const handleReset = (e) => {
        handleSetValue('', () => {
            innerRef.current?.focus();
        });
        resolveOnChange(innerRef.current?.resizableTextArea?.textArea, e, onChange);
    };
    const prefixCls = getPrefixCls('input', customizePrefixCls);
    React.useImperativeHandle(ref, () => ({
        resizableTextArea: innerRef.current?.resizableTextArea,
        focus: (option) => {
            triggerFocus(innerRef.current?.resizableTextArea?.textArea, option);
        },
        blur: () => innerRef.current?.blur()
    }));
    const customClassName = getInputClassNames(props, !!animation?.breathing, prefixCls);
    const textArea = (React.createElement(RcTextArea, { ...omit(props, ['allowClear']), className: classNames({
            [className]: className && !showCount,
            [`${prefixCls}-sm`]: size === 'small' || customizeSize === 'small',
            [`${prefixCls}-lg`]: size === 'large' || customizeSize === 'large',
            [`${prefixCls}-xs`]: customizeSize === 'xs',
            [`${prefixCls}-breathing`]: animation?.breathing
        }, customClassName), style: showCount ? undefined : style, prefixCls: prefixCls, onCompositionStart: onInternalCompositionStart, onChange: handleChange, onCompositionEnd: onInternalCompositionEnd, ref: innerRef, maxLength: maxLength }));
    let val = fixControlledValue(value);
    if (!compositing && hasMaxLength && (props.value === null || props.value === undefined)) {
        // fix #27612 将value转为数组进行截取，解决 '😂'.length === 2 等emoji表情导致的截取乱码的问题
        val = fixEmojiLength(val, maxLength);
    }
    // TextArea
    const textareaNode = (React.createElement(ClearableLabeledInput, { ...props, prefixCls: prefixCls, direction: direction, inputType: 'text', value: val, element: textArea, handleReset: handleReset, ref: clearableInputRef, bordered: true, style: showCount ? undefined : style }));
    // Only show text area wrapper when needed
    if (showCount) {
        const valueLength = [...val].length;
        let dataCount = '';
        if (typeof showCount === 'object') {
            dataCount = showCount.formatter({ count: valueLength, maxLength });
        }
        else {
            dataCount = `${valueLength}${hasMaxLength ? ` / ${maxLength}` : ''}`;
        }
        return (React.createElement("div", { className: classNames(`${prefixCls}-textarea`, {
                [`${prefixCls}-textarea-rtl`]: direction === 'rtl',
                [`${prefixCls}-textarea-sm`]: size === 'small' || customizeSize === 'small',
                [`${prefixCls}-textarea-lg`]: size === 'large' || customizeSize === 'large',
                [`${prefixCls}-textarea-xs`]: customizeSize === 'xs',
                [`${prefixCls}-textarea-clear-show-count`]: props.allowClear
            }, `${prefixCls}-textarea-show-count`, className), style: style, "data-count": dataCount }, textareaNode));
    }
    return textareaNode;
});
export default TextArea;
